import { Disclosure } from "@headlessui/react";
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronUpDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { useActiveFilters, FilterEntry } from "../../../hooks/useActiveFilters";
import { FilterBadge } from "../../Shared/Filter/ActiveFilterDisplay";
import ReactDatePicker from "react-datepicker";
import { City, RoleStatusCandidateCount, University, UniversityCompany } from "../../../Models";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import useApiToken from "../../../hooks/useApiToken";
import { useParams } from "react-router-dom";
import { getUniversitiesForFilter, getUniversityBranchesForRole, searchCompanies } from "../../../api/masterdata.api";
import { getCompanyRankingCategories, getUniversityRankingCategories } from "../../../api/ranking.api";

const CANDIDATE_FILTERS_STORAGE_KEY = 'candidateSidebarFilters';

export type filterOption = {
  value: string | number | boolean | null;
  label: string;
  checked: boolean;
  extraData?: number;
};
export interface IFilter {
  id: string;
  name: string;
  options: filterOption[];
}
export interface IGradeFilter extends IFilter {
  roleStatusTypeName?: string;
}
interface IRangeFilter {
  id: string;
  name: string;
  minVal: number;
  maxVal: number;
  currentVal: { min: number; max: number };
  // Slider granularity. Scores are stored as decimals (e.g. 82.35), so leaving these
  // filters on rc-slider's default step of 1 made every fractional score unreachable.
  step?: number;
  // Shows the typed min/max boxes — needed wherever step < 1, since dragging a slider
  // cannot land on an arbitrary decimal.
  allowDecimalInput?: boolean;
}

interface IlocationFilter extends IFilter {
  filteredOptions: filterOption[];
}
const initialFilters: IFilter[] = [
  {
    id: "latestStatusId",
    name: "Status",
    options: [{ value: "", label: "All", checked: true, extraData: 0 }],
  },

  {
    id: "universityRankings",
    name: "University Ranking",
    options: [
      { value: 1, label: "1-100", checked: false },
      { value: 2, label: "101-200", checked: false },
      { value: 0, label: "unranked", checked: false },
    ],
  },
  {
    id: "companyRankings",
    name: "Company Ranking",
    options: [
      { value: 1, label: "1-100", checked: false },
      { value: 2, label: "101-200", checked: false },
      { value: 0, label: "unranked", checked: false },
    ],
  },
  {
    id: "branches",
    name: "Education Branch",
    options: [{ value: "", label: "All", checked: true }],
  },
  {
    id: "isShippingCompany",
    name: "ShippingCompany",
    options: [
      { value: false, label: "Related to Shipping", checked: false },
      { value: true, label: "Shipping Company", checked: false },
    ],
  },
  {
    id: "current",
    name: "Position",
    options: [
      { value: "working", label: "Working", checked: false },
      { value: "studying", label: "Studying", checked: false },
      { value: null, label: "None", checked: false },
    ],
  },
  {
    id: "assignmentSubmission",
    name: "Assignment",
    options: [
      { value: true, label: "Submitted", checked: false },
      { value: null, label: "Not Submitted", checked: false },
    ],
  },
  {
    id: "reApply",
    name: "Application",
    options: [
      { value: null, label: "Single", checked: false },
      { value: true, label: "Multiple", checked: false },
    ],
  },
  {
    id: "slotBookingStatus",
    name: "Interview Slot",
    options: [
      { value: "all", label: "All", checked: true },
      { value: "booked", label: "Booked", checked: false },
      { value: "notBooked", label: "Not Booked", checked: false },
    ],
  },
  // Must stay LAST: stored filters are restored by index, so appending keeps every filter a
  // recruiter already saved lined up with its slot. Values mirror ApplicationCategories.
  // Goes through the generic "applicationCategory eq ..." path: the server applies these
  // filters to the role-scoped CandidateAssessmentDto list, which carries the category directly.
  {
    id: "applicationCategory",
    name: "QGrade Category",
    options: [
      { value: "all", label: "All", checked: true },
      { value: "Good", label: "Good", checked: false },
      { value: "Relevant", label: "Relevant", checked: false },
      { value: "NotRelevant", label: "Not relevant", checked: false },
      { value: "NotScored", label: "Not scored", checked: false },
    ],
  },
];

const initialRangeFilters: IRangeFilter[] = [
  {
    id: "yearsOfExperience",
    name: "YOE",
    minVal: 0,
    maxVal: 50,
    currentVal: {
      min: 0,
      max: 50,
    },
  },
  {
    id: "age",
    name: "Age",
    minVal: 0,
    maxVal: 100,
    currentVal: {
      min: 0,
      max: 100,
    },
  },
  {
    id: "experienceInMonths",
    name: "Exp",
    minVal: 0,
    maxVal: 60,
    currentVal: {
      min: 0,
      max: 60,
    },
  },
  {
    id: "score",
    name: "Assessment Score",
    minVal: 0,
    maxVal: 100,
    currentVal: {
      min: 0,
      max: 100,
    },
    step: 0.1,
    allowDecimalInput: true,
  },
  {
    id: "cvScore",
    name: "CV Score",
    minVal: 0,
    maxVal: 100,
    currentVal: {
      min: 0,
      max: 100,
    },
    step: 0.1,
    allowDecimalInput: true,
  },
  // Weighted application-question score (%). Nullable on the server - candidates who couldn't be
  // scored are excluded once the range is narrowed, and included while it's left at 0-100
  // (an untouched range filter isn't sent at all).
  {
    id: "applicationScore",
    name: "QGrade",
    minVal: 0,
    maxVal: 100,
    currentVal: {
      min: 0,
      max: 100,
    },
    step: 0.1,
    allowDecimalInput: true,
  },
];

// Rounds to 2 dp so dragging a 0.1-step slider stores 82.3 rather than 82.30000000000001,
// which would otherwise leak into the filter query sent to the API.
const roundScore = (n: number) => Math.round(n * 100) / 100;

const initialLocationFilter: IlocationFilter = {
  id: "workLocation1City.Id",
  name: "Location",
  options: [],
  filteredOptions: [],
};

const initialUniversityNameFilter: IlocationFilter = {
  id: "universityName",
  name: "University Name",
  options: [],
  filteredOptions: [],
};

const initialCompanyNameFilter: IlocationFilter = {
  id: "companyName",
  name: "Company Name",
  options: [],
  filteredOptions: [],
};

const initialDateFilter: {
  id: string;
  name: string;
  startValue: Date | null;
  endValue: Date | null;
} = {
  id: "latestStatusDate",
  name: "Date",

  startValue: null,
  endValue: null,
};
const ranks = [
  "Master",
  "Chief Officer",
  "Second Officer",
  "Third Officer",
  "Junior Officer",
  "Deck Cadet",
  "Chief Engineer",
  "Second Engineer",
  "Third Engineer",
  "Fourth Engineer",
  "Junior Engineer",
  "Engine Cadet",
  "Bosun",
  "AB",
  "OS",
  "Motorman",
  "Oiler",
  "Wiper",
  "Pumpman",
  "Fitter",
  "Welder",
  "ETR",
  "Cook",
  "Assistant Cook",
  "Messman",
  "Catering",
  "ETO",
  "Electrician Cadet",
];

const shippingDepartments = [
  "Technical",
  "Purchasing",
  "QHSE",
  "Operations",
  "Accounting",
  "Crewing",
  "Chartering",
  "IT",
  "PMS",
  "Vessel Performance",
];
type props = {
  cities: City[];
  status: RoleStatusCandidateCount;
  rankType: { id: string; name: string };
  handleSearchQuery: (query: string) => void;
  handleFilterQuery: (query: string) => void;
  handleStatusFilterIsAll: (isAll: boolean) => void;
  setSelectedCol: React.Dispatch<React.SetStateAction<string>>;
  isShipping: boolean;
};

const saveFiltersToStorage = (filterData: any, roleId: string | undefined) => {
  try {
    const key = roleId ? `${CANDIDATE_FILTERS_STORAGE_KEY}_${roleId}` : CANDIDATE_FILTERS_STORAGE_KEY;
    const dataToStore = {
      ...filterData,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(dataToStore));
  } catch (error) {
    console.error('Failed to save filters to localStorage:', error);
  }
};

const loadFiltersFromStorage = (roleId: string | undefined) => {
  try {
    const key = roleId ? `${CANDIDATE_FILTERS_STORAGE_KEY}_${roleId}` : CANDIDATE_FILTERS_STORAGE_KEY;
    const storedData = localStorage.getItem(key);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (error) {
    console.error('Failed to load filters from localStorage:', error);
  }
  return null;
};

// Overlay stored range-filter values onto the current initialRangeFilters (by id) so a
// filter added to the code after a user already saved filters for this role (e.g. cvScore)
// still shows up instead of being wiped out by an older stored array.
// Only currentVal comes from storage — the config (bounds, step, name) always comes from
// code, otherwise a stored entry saved before `step` existed would pin the slider back to
// whole numbers. Values are clamped in case the bounds changed since they were saved.
const mergeRangeFilters = (stored: IRangeFilter[] | undefined): IRangeFilter[] => {
  const storedById = new Map((stored ?? []).map((f) => [f.id, f]));
  return initialRangeFilters.map((f) => {
    const prev = storedById.get(f.id);
    if (!prev?.currentVal) return f;
    const clamp = (n: number) => Math.min(Math.max(n, f.minVal), f.maxVal);
    return {
      ...f,
      currentVal: {
        min: clamp(prev.currentVal.min),
        max: clamp(prev.currentVal.max),
      },
    };
  });
};

const clearFiltersFromStorage = (roleId: string | undefined) => {
  try {
    const key = roleId ? `${CANDIDATE_FILTERS_STORAGE_KEY}_${roleId}` : CANDIDATE_FILTERS_STORAGE_KEY;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear filters from localStorage:', error);
  }
};

/**
 * Typed min/max entry for range filters that accept decimals. A slider handle can only land
 * on a step boundary, so recruiters filtering on scores like 82.35 need to type the value.
 * Keeps a local draft and commits on blur / Enter so a half-typed value ("82.") is not
 * rejected mid-keystroke.
 */
function RangeNumberInputs({
  section,
  onCommit,
}: {
  section: IRangeFilter;
  onCommit: (value: { min: number; max: number }) => void;
}) {
  const [draft, setDraft] = useState({
    min: String(section.currentVal.min),
    max: String(section.currentVal.max),
  });

  // Re-sync when the slider (or a filter reset) changes the range from outside.
  useEffect(() => {
    setDraft({
      min: String(section.currentVal.min),
      max: String(section.currentVal.max),
    });
  }, [section.currentVal.min, section.currentVal.max]);

  const commit = () => {
    const clamp = (n: number) =>
      Math.min(Math.max(n, section.minVal), section.maxVal);
    const parsedMin = parseFloat(draft.min);
    const parsedMax = parseFloat(draft.max);
    const a = clamp(Number.isFinite(parsedMin) ? parsedMin : section.minVal);
    const b = clamp(Number.isFinite(parsedMax) ? parsedMax : section.maxVal);
    // Swap rather than reject, so typing the bounds in either order still works.
    const next = {
      min: roundScore(Math.min(a, b)),
      max: roundScore(Math.max(a, b)),
    };
    setDraft({ min: String(next.min), max: String(next.max) });
    onCommit(next);
  };

  const inputClass =
    "w-20 rounded-md border-0 px-2 py-1 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-entntorange bg-transparent dark:text-gray-300 dark:ring-gray-500";

  return (
    <div className="mt-4 flex items-center gap-2">
      <input
        type="number"
        aria-label={`${section.name} minimum`}
        className={inputClass}
        step={section.step ?? 1}
        min={section.minVal}
        max={section.maxVal}
        value={draft.min}
        onChange={(e) => setDraft((p) => ({ ...p, min: e.target.value }))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
      <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
      <input
        type="number"
        aria-label={`${section.name} maximum`}
        className={inputClass}
        step={section.step ?? 1}
        min={section.minVal}
        max={section.maxVal}
        value={draft.max}
        onChange={(e) => setDraft((p) => ({ ...p, max: e.target.value }))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
    </div>
  );
}

export default function RoleSidebarCandidate({
  cities,
  status,
  rankType,
  handleSearchQuery,
  handleFilterQuery,
  handleStatusFilterIsAll,
  isShipping,
}: props) {
  const [rangeFilters, setRangeFilter] = useState(initialRangeFilters);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [locationFilter, setLocationFilter] = useState(initialLocationFilter);
  const [locationSearch, setLocationSearch] = useState("");
  const [universityNameFilter, setUniversityNameFilter] = useState(initialUniversityNameFilter);
  const [universityNameSearch, setUniversityNameSearch] = useState("");
  const [companyNameFilter, setCompanyNameFilter] = useState(initialCompanyNameFilter);
  const [companyNameSearch, setCompanyNameSearch] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [gradeFilters, setGradeFilters] = useState<IGradeFilter[]>([]);
  const [resetFilter, setResetFilter] = useState(initialFilters);
  const [currentRangeFilters, setCurrentRangeFilter] =
    useState(initialRangeFilters);
  const [selectedRank, setSelectedRank] = useState<string>();
  const [selectedRanks, setSelectedRanks] = useState<string[]>();
  const [selectedShippingDepartment, setSelectedShippingDepartment] =
    useState<string>();
  const [selectedShippingDepartments, setSelectedShippingDepartments] =
    useState<string[]>();
  const [isOnBoardExp, setIsOnBoardExp] = useState<boolean>();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  // Which assessment the "Assessment Score" range applies to. "" = the candidate's blended
  // role score (the old, ambiguous behaviour); a roleStatusId targets that stage's own score.
  const [scoreAssessmentId, setScoreAssessmentId] = useState<string>("");

  // Lazy loading states
  const [universities, setUniversities] = useState<University[]>([]);
  const [companies, setCompanies] = useState<UniversityCompany[]>([]);
  const [universitiesLoading, setUniversitiesLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [universitiesLoaded, setUniversitiesLoaded] = useState(false);

  const token = useApiToken();
  const { roleId } = useParams();
  const isInitialMount = useRef(true);
  const loadedFromStorage = useRef(false);
  const restoredFilters = useRef<any>(null);
  const companySearchInputRef = useRef<HTMLInputElement>(null);
  const wasCompaniesLoading = useRef(false);

  useEffect(() => {
    const storedData = loadFiltersFromStorage(roleId);
    if (storedData) {
      loadedFromStorage.current = true;
      restoredFilters.current = storedData;
      if (storedData.rangeFilters) setRangeFilter(mergeRangeFilters(storedData.rangeFilters));
      if (storedData.currentRangeFilters) setCurrentRangeFilter(mergeRangeFilters(storedData.currentRangeFilters));
      if (storedData.dateFilter) {
        setDateFilter({
          ...storedData.dateFilter,
          startValue: storedData.dateFilter.startValue ? new Date(storedData.dateFilter.startValue) : null,
          endValue: storedData.dateFilter.endValue ? new Date(storedData.dateFilter.endValue) : null,
        });
      }
      if (typeof storedData.scoreAssessmentId === "string")
        setScoreAssessmentId(storedData.scoreAssessmentId);
      if (storedData.selectedRanks) setSelectedRanks(storedData.selectedRanks);
      if (storedData.selectedShippingDepartments) setSelectedShippingDepartments(storedData.selectedShippingDepartments);
      if (storedData.isOnBoardExp !== undefined) setIsOnBoardExp(storedData.isOnBoardExp);
      if (storedData.universityNameFilter?.options?.length > 0) {
        const checkedOptions = storedData.universityNameFilter.options.filter((o: any) => o.checked);
        if (checkedOptions.length > 0) {
          setUniversityNameFilter({
            ...initialUniversityNameFilter,
            options: checkedOptions,
            filteredOptions: checkedOptions,
          });
        }
      }
      if (storedData.companyNameFilter?.options?.length > 0) {
        const checkedOptions = storedData.companyNameFilter.options.filter((o: any) => o.checked);
        if (checkedOptions.length > 0) {
          setCompanyNameFilter({
            ...initialCompanyNameFilter,
            options: checkedOptions,
            filteredOptions: checkedOptions,
          });
        }
      }
    }
  }, [roleId]);

  useEffect(() => {
    if (loadedFromStorage.current &&
      restoredFilters.current?.universityNameFilter?.options?.some((o: any) => o.checked)) {
      loadUniversities();
    }
  }, []);

  useEffect(() => {
    let filteredCity = cities;
    if (locationSearch !== "")
      filteredCity = cities.filter((c) =>
        c.name.toLowerCase().startsWith(locationSearch.toLowerCase())
      );
    const filteredCities = locationFilter.options.filter((lf) => {
      return filteredCity.some((fc) => fc.id === lf.value);
    });

    setLocationFilter((prev) => ({ ...prev, filteredOptions: filteredCities }));
  }, [locationSearch, cities, locationFilter.options]);

  useEffect(() => {
    let filteredUniversities = universityNameFilter.options;
    if (universityNameSearch !== "") {
      filteredUniversities = universityNameFilter.options.filter((u) =>
        u.label.toLowerCase().includes(universityNameSearch.toLowerCase())
      );
    }

    setUniversityNameFilter((prev) => ({ ...prev, filteredOptions: filteredUniversities }));
  }, [universityNameSearch, universityNameFilter.options]);

  // Dynamic company search with debouncing
  useEffect(() => {
    const searchCompaniesDebounced = async () => {
      if (!token) return;

      if (companyNameSearch.length < 2) {
        // Show only selected companies if search is less than 2 chars
        const selectedOnly = companyNameFilter.options.filter(opt => opt.checked);
        setCompanyNameFilter((prev) => ({ ...prev, filteredOptions: selectedOnly }));
        setCompaniesLoading(false);
        return;
      }

      setCompaniesLoading(true);
      try {
        const results = await searchCompanies(companyNameSearch, token, 50);
        setCompanies(results);
      } catch (error) {
        console.error('Error searching companies:', error);
      } finally {
        setCompaniesLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCompaniesDebounced, 300);
    return () => clearTimeout(timeoutId);
  }, [companyNameSearch, token]);

  useEffect(() => {
    if (wasCompaniesLoading.current && !companiesLoading) {
      setTimeout(() => {
        companySearchInputRef.current?.focus();
      }, 0);
    }
    wasCompaniesLoading.current = companiesLoading;
  }, [companiesLoading]);

  useEffect(() => {
    setFilters((prevFilters) => {
      const filterStatusUpdate = [...prevFilters];
      const updatedOptions: filterOption[] = [
        { value: "", label: "All", checked: true, extraData: 0 },
      ];
      status.roleStatuses.forEach((s) => {
        updatedOptions.push({
          checked: false,
          value: s.id,
          label: s.name,
          extraData: s.candidatesCount as number,
        });
      });
      filterStatusUpdate[0].options = updatedOptions;
      filterStatusUpdate[0].options[0].extraData = status.totalCandidateCount;
      setResetFilter(filterStatusUpdate);

      if (loadedFromStorage.current && restoredFilters.current?.filters) {
        const restored = restoredFilters.current.filters;
        return filterStatusUpdate.map((filter, idx) => {
          const restoredFilter = restored[idx];
          if (restoredFilter && restoredFilter.id === filter.id) {
            return {
              ...filter,
              options: filter.options.map(opt => {
                const restoredOpt = restoredFilter.options.find((ro: any) => ro.value === opt.value);
                return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
              })
            };
          }
          return filter;
        });
      }

      return filterStatusUpdate;
    });
  }, [status, cities]);


  useEffect(() => {
    if (!token) return;
    const setRanking = async () => {
      const companyRankings = await getCompanyRankingCategories(
        rankType.id,
        token
      );
      const universityRankings = await getUniversityRankingCategories(
        rankType.id,
        token
      );
      setFilters((prevFilters) => {
        const filterStatusUpdate = [...prevFilters];
        const companyRankingOptions: filterOption[] = [];
        const universityRankingOptions: filterOption[] = [];

        companyRankings.forEach((cr) => {
          companyRankingOptions.push({
            checked: false,
            value: cr.rankNumber,
            label: cr.title,
          });
        });

        universityRankings.forEach((ur) => {
          universityRankingOptions.push({
            checked: false,
            value: ur.rankNumber,
            label: ur.title,
          });
        });

        filterStatusUpdate[1].options = universityRankingOptions;
        filterStatusUpdate[2].options = companyRankingOptions;

        setResetFilter(filterStatusUpdate);

        if (loadedFromStorage.current && restoredFilters.current?.filters) {
          const restored = restoredFilters.current.filters;
          return filterStatusUpdate.map((filter, idx) => {
            const restoredFilter = restored[idx];
            if (restoredFilter && restoredFilter.id === filter.id) {
              return {
                ...filter,
                options: filter.options.map(opt => {
                  const restoredOpt = restoredFilter.options.find((ro: any) => ro.value === opt.value);
                  return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
                })
              };
            }
            return filter;
          });
        }

        return filterStatusUpdate;
      });
    };

    setRanking();
  }, [token, rankType]);

  useEffect(() => {
    if (!roleId) return;

    getUniversityBranchesForRole(roleId, token || "").then((branch) => {
      let updatedFilter = [...filters];
      const branchFilterIndex = updatedFilter.findIndex(
        (f) => f.id === "branches"
      );

      if (branch && branch.length > 0) {
        const branchOptions = branch.map((br) => ({
          value: br.id,
          label: br.name,
          checked: false,
        }));

        if (branchFilterIndex >= 0) {
          updatedFilter[branchFilterIndex].options = [
            { value: "", label: "All", checked: true },
            ...branchOptions,
          ];
        } else {
          updatedFilter.push({
            id: "branches",
            name: "Branches",
            options: [
              { value: "", label: "All", checked: true },
              ...branchOptions,
            ],
          });
        }
      } else {
        if (branchFilterIndex >= 0) {
          updatedFilter.splice(branchFilterIndex, 1);
        }
      }

      if (loadedFromStorage.current && restoredFilters.current?.filters) {
        const restored = restoredFilters.current.filters;
        updatedFilter = updatedFilter.map((filter) => {
          const restoredFilter = restored.find((rf: any) => rf.id === filter.id);
          if (restoredFilter) {
            return {
              ...filter,
              options: filter.options.map(opt => {
                const restoredOpt = restoredFilter.options.find((ro: any) => ro.value === opt.value);
                return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
              })
            };
          }
          return filter;
        });
      }

      setFilters(updatedFilter);
    });
  }, [roleId]);

  useEffect(() => {
    if (!status.roleStatuses) return;
    // Only include Interview and Assignment types for manual grading filter
    let filter: IGradeFilter[] = status.roleStatuses
      .slice(1, -2)
      .filter((rs) => rs.roleStatusType?.name === "Interview" || rs.roleStatusType?.name === "Assignment")
      .map((rs) => ({
        id: rs.id,
        name: rs.name,
        roleStatusTypeName: rs.roleStatusType?.name,
        options: [
          { value: 100, label: "Excellent", checked: false },
          { value: 89, label: "Pass", checked: false },
          { value: 79, label: "Fail", checked: false },
          { value: 50, label: "Waitlist", checked: false },
        ],
      }));

    if (loadedFromStorage.current && restoredFilters.current?.gradeFilters) {
      const restored = restoredFilters.current.gradeFilters;
      filter = filter.map((f) => {
        const restoredFilter = restored.find((rf: any) => rf.id === f.id);
        if (restoredFilter) {
          return {
            ...f,
            options: f.options.map(opt => {
              const restoredOpt = restoredFilter.options.find((ro: any) => ro.value === opt.value);
              return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
            })
          };
        }
        return f;
      });
    }

    setGradeFilters(filter);
  }, [status]);

  useEffect(() => {
    const updatedLocationFilterOption = cities.map((c) => ({
      checked: false,
      value: c.id,
      label: c.name,
    }));

    let locationCityFilter = {
      ...initialLocationFilter,
      options: updatedLocationFilterOption,
    };

    if (loadedFromStorage.current && restoredFilters.current?.locationFilter) {
      const restored = restoredFilters.current.locationFilter;
      locationCityFilter = {
        ...locationCityFilter,
        options: locationCityFilter.options.map(opt => {
          const restoredOpt = restored.options.find((ro: any) => ro.value === opt.value);
          return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
        }),
        filteredOptions: locationCityFilter.options.map(opt => {
          const restoredOpt = restored.options.find((ro: any) => ro.value === opt.value);
          return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
        })
      };
    }

    setLocationFilter(locationCityFilter);
  }, [cities]);

  // useEffect(() => {
  //   // Only process if we have universities loaded
  //   if (universities.length === 0) return;

  //   const updatedUniversityFilterOption = universities.map((u) => ({
  //     checked: false,
  //     value: u.id,
  //     label: u.name,
  //   }));

  //   let universityFilter = {
  //     ...initialUniversityNameFilter,
  //     options: updatedUniversityFilterOption,
  //     filteredOptions: updatedUniversityFilterOption,
  //   };

  //   if (loadedFromStorage.current && restoredFilters.current?.universityNameFilter) {
  //     const restored = restoredFilters.current.universityNameFilter;
  //     universityFilter = {
  //       ...universityFilter,
  //       options: universityFilter.options.map(opt => {
  //         const restoredOpt = restored.options.find((ro: any) => ro.value === opt.value);
  //         return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
  //       }),
  //       filteredOptions: universityFilter.options.map(opt => {
  //         const restoredOpt = restored.options.find((ro: any) => ro.value === opt.value);
  //         return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
  //       })
  //     };
  //   }

  //   setUniversityNameFilter(universityFilter);
  // }, [universities]);

  useEffect(() => {
    if (universities.length === 0) return;

    const updatedUniversityFilterOption = universities.map((u) => ({
      checked: false,
      value: u.id,
      label: u.name,
    }));
    const existingChecked = universityNameFilter.options.filter(opt => opt.checked);
    const mergedOptions = [...existingChecked];

    updatedUniversityFilterOption.forEach(newOpt => {
      const existing = mergedOptions.find(opt => opt.value === newOpt.value);
      if (existing) {
        return;
      } else {
        mergedOptions.push(newOpt);
      }
    });

    let universityFilter = {
      ...universityNameFilter,
      options: mergedOptions,
      filteredOptions: mergedOptions,
    };

    if (loadedFromStorage.current && restoredFilters.current?.universityNameFilter) {
      const restored = restoredFilters.current.universityNameFilter;
      universityFilter = {
        ...universityFilter,
        options: universityFilter.options.map(opt => {
          const restoredOpt = restored.options.find((ro: any) => ro.value === opt.value);
          return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
        }),
        filteredOptions: universityFilter.options.map(opt => {
          const restoredOpt = restored.options.find((ro: any) => ro.value === opt.value);
          return restoredOpt ? { ...opt, checked: restoredOpt.checked } : opt;
        })
      };
    }

    setUniversityNameFilter(universityFilter);
  }, [universities]);

  // useEffect(() => {
  //   // Only process if we have companies loaded from search
  //   if (companies.length === 0) return;

  //   const uniqueCompanies = Array.from(
  //     new Map(companies.map((c: any) => [c.id, c])).values()
  //   );

  //   const updatedCompanyFilterOption = uniqueCompanies.map((c: any) => ({
  //     checked: companyNameFilter.options.find(opt => opt.value === c.id)?.checked || false,
  //     value: c.id,
  //     label: c.name,
  //   }));

  //   // Merge with existing checked items to ensure selected items persist
  //   const existingChecked = companyNameFilter.options.filter(opt => opt.checked);
  //   const mergedOptions = [...existingChecked];

  //   updatedCompanyFilterOption.forEach(newOpt => {
  //     if (!mergedOptions.find(opt => opt.value === newOpt.value)) {
  //       mergedOptions.push(newOpt);
  //     }
  //   });

  //   setCompanyNameFilter(prev => ({
  //     ...prev,
  //     options: mergedOptions,
  //     filteredOptions: updatedCompanyFilterOption,
  //   }));
  // }, [companies]);

  useEffect(() => {
    if (companies.length === 0) return;

    const uniqueCompanies = Array.from(
      new Map(companies.map((c: any) => [c.id, c])).values()
    );
    const updatedCompanyFilterOption = uniqueCompanies.map((c: any) => ({
      checked: companyNameFilter.options.find(opt => opt.value === c.id)?.checked || false,
      value: c.id,
      label: c.name,
    }));
    const existingChecked = companyNameFilter.options.filter(opt => opt.checked);
    const mergedOptions = [...existingChecked];
    updatedCompanyFilterOption.forEach(newOpt => {
      const existing = mergedOptions.find(opt => opt.value === newOpt.value);
      if (existing) {
        Object.assign(existing, { ...newOpt, checked: existing.checked });
      } else {
        mergedOptions.push(newOpt);
      }
    });

    setCompanyNameFilter(prev => ({
      ...prev,
      options: mergedOptions,
      filteredOptions: updatedCompanyFilterOption,
    }));
  }, [companies]);

  useEffect(() => {
    let query: string[] = [];
    filters.forEach((f) => {
      let sameFilter: string[] = [];
      f.options.forEach((fo) => {
        if (fo.value !== "" && fo.value !== "all")
          if (fo.checked) {
            sameFilter.push(
              `${f.id} ${f.id === "universityRankings" ||
                f.id === "companyRankings" ||
                f.id === "branches"
                ? "contains"
                : fo.value === true
                  ? "ne"
                  : "eq"
              } ${typeof fo.value === "string"
                ? `"${fo.value}"`
                : fo.value === true
                  ? null
                  : fo.value
              }`
            );
          }
      });
      if (sameFilter.length > 0)
        query.push("(" + sameFilter.join(" or ") + ")");
    });

    gradeFilters.forEach((g) => {
      let sameFilter: string[] = [];
      g.options.forEach((go) => {
        if (go.checked) {
          // Check if this is Interview or Assignment type - use manual evaluations
          if (g.roleStatusTypeName === "Interview" || g.roleStatusTypeName === "Assignment") {
            // Map grade values to rating strings
            const rating = go.value === 100 ? "Excellent" : go.value === 89 ? "Pass" : go.value === 79 ? "Fail" : "Waitlist";
            sameFilter.push(
              `manualEvaluations any (roleStatusId,"${g.id}",rating,"${rating}")`
            );
          } else {
            // For Assessment types, use old candidateStatuses with grade
            sameFilter.push(
              `candidateStatuses any (roleStatusId,"${g.id}",grade,${go.value})`
            );
          }
        }
      });
      if (sameFilter.length > 0)
        query.push("(" + sameFilter.join(" or ") + ")");
    });

    let lf: string[] = [];
    locationFilter.options.forEach((fo) => {
      if (fo.checked)
        lf.push(
          `workLocation1City.Id eq "${fo.value}" or workLocation2City.Id eq "${fo.value}"`
        );
    });
    if (lf.length > 0) query.push("(" + lf.join(" or ") + ")");

    let unf: string[] = [];
    universityNameFilter.options.forEach((fo) => {
      if (fo.checked) {
        const universityFilter = `education any (University.Id,"${fo.value}")`;
        // console.log("[FRONTEND FILTER DEBUG] Adding university filter:", universityFilter);
        unf.push(universityFilter);
      }
    });
    if (unf.length > 0) {
      const universityQuery = "(" + unf.join(" or ") + ")";
      // console.log("[FRONTEND FILTER DEBUG] Combined university query:", universityQuery);
      query.push(universityQuery);
    }

    let cnf: string[] = [];
    companyNameFilter.options.forEach((fo) => {
      if (fo.checked) {
        const companyFilter = `experience any (CompanyLocation.CompanyId,"${fo.value}")`;
        // console.log("[FRONTEND FILTER DEBUG] Adding company filter:", companyFilter);
        cnf.push(companyFilter);
      }
    });
    if (cnf.length > 0) {
      const companyQuery = "(" + cnf.join(" or ") + ")";
      // console.log("[FRONTEND FILTER DEBUG] Combined company query:", companyQuery);
      query.push(companyQuery);
    }

    rangeFilters.forEach((f) => {
      if (f.currentVal.min !== f.minVal || f.currentVal.max !== f.maxVal) {
        // When a specific assessment is picked, target that stage's own score
        // (assessmentScore_<roleStatusId>) instead of the blended role-level `score`.
        const fieldId =
          f.id === "score" && scoreAssessmentId
            ? `assessmentScore_${scoreAssessmentId}`
            : f.id;
        query.push(
          `${fieldId} gte ${f.currentVal.min} AND ${fieldId} ste ${f.currentVal.max}`
        );
      }
    });
    if (dateFilter.endValue && dateFilter.startValue)
      query.push(
        `${dateFilter.id} gte "${dateFilter.startValue.toISOString()}" AND ${dateFilter.id
        } ste "${dateFilter.endValue.toISOString()}"`
      );
    let expQuery = "";
    if (isOnBoardExp !== undefined) {
      if (isOnBoardExp) {
        expQuery = "isOnboard eq true";
        if (selectedRanks && selectedRanks.length > 0) {
          let selectedRankQuery: string[] = [];
          selectedRanks.forEach((sr) => {
            selectedRankQuery.push(`shippingRanks contains "${sr}"`);
          });
          expQuery += ` AND (${selectedRankQuery.join(" or ")})`;
        }
      } else {
        expQuery = "isOnboard eq false";
        if (
          selectedShippingDepartments &&
          selectedShippingDepartments.length > 0
        ) {
          let ssdQuery: string[] = [];
          selectedShippingDepartments.forEach((ssd) => {
            ssdQuery.push(`shippingDepartment contains "${ssd}"`);
          });
          expQuery += ` AND (${ssdQuery.join(" or ")})`;
        }
      }
    }
    var _query = query.join(" AND ");
    if (_query.length === 0) _query = expQuery;
    else if (expQuery.length > 0) _query += " AND " + expQuery;


    handleFilterQuery(_query);
  }, [
    filters,
    rangeFilters,
    dateFilter,
    locationFilter,
    universityNameFilter,
    companyNameFilter,
    handleFilterQuery,
    gradeFilters,
    isOnBoardExp,
    selectedRanks,
    selectedShippingDepartments,
    scoreAssessmentId,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const filterData = {
      filters,
      rangeFilters,
      currentRangeFilters,
      dateFilter,
      locationFilter,
      universityNameFilter,
      companyNameFilter,
      gradeFilters,
      selectedRanks,
      selectedShippingDepartments,
      isOnBoardExp,
      scoreAssessmentId
    };

    saveFiltersToStorage(filterData, roleId);
  }, [
    filters,
    rangeFilters,
    currentRangeFilters,
    dateFilter,
    locationFilter,
    universityNameFilter,
    companyNameFilter,
    gradeFilters,
    selectedRanks,
    selectedShippingDepartments,
    isOnBoardExp,
    scoreAssessmentId,
    roleId
  ]);

  useEffect(() => {
    const sectionsToOpen: Record<string, boolean> = {};

    filters.forEach((filter) => {
      const hasSelection = filter.options.some(opt => opt.checked && opt.value !== "");
      if (hasSelection) {
        sectionsToOpen[filter.id] = true;
      }
    });

    rangeFilters.forEach((filter) => {
      if (filter.currentVal.min !== filter.minVal || filter.currentVal.max !== filter.maxVal) {
        sectionsToOpen[filter.id] = true;
      }
    });

    if (locationFilter.options.some(opt => opt.checked)) {
      sectionsToOpen[locationFilter.id] = true;
    }

    if (dateFilter.startValue || dateFilter.endValue) {
      sectionsToOpen[dateFilter.id] = true;
    }

    if (gradeFilters.some(gf => gf.options.some(opt => opt.checked))) {
      sectionsToOpen['grade'] = true;
    }

    if (universityNameFilter.options.some(opt => opt.checked)) {
      sectionsToOpen[universityNameFilter.id] = true;
    }

    if (companyNameFilter.options.some(opt => opt.checked)) {
      sectionsToOpen[companyNameFilter.id] = true;
    }

    if (isOnBoardExp !== undefined || selectedRanks?.length || selectedShippingDepartments?.length) {
      sectionsToOpen['shippingExp'] = true;
    }

    setOpenSections(prev => {
      const changed = Object.keys(sectionsToOpen).some(key => sectionsToOpen[key] !== prev[key]) ||
        Object.keys(prev).some(key => prev[key] !== sectionsToOpen[key]);
      return changed ? sectionsToOpen : prev;
    });
  }, [filters, rangeFilters, locationFilter, universityNameFilter, companyNameFilter, dateFilter, gradeFilters, isOnBoardExp, selectedRanks, selectedShippingDepartments]);

  // One option per pipeline stage that has an assessment attached — same predicate the
  // candidates table uses to build its per-assessment score columns, so the dropdown always
  // mirrors the columns the recruiter is looking at.
  const assessmentScoreOptions = useMemo(
    () =>
      (status.roleStatuses ?? [])
        .filter((rs) => rs.assessmentTemplateId || rs.assessmentId)
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((rs) => ({ id: rs.id, name: rs.name })),
    [status]
  );

  // A stored selection can point at a stage that no longer has an assessment (or belongs to
  // another role) — fall back to the blended score instead of filtering on a dead id.
  useEffect(() => {
    if (
      scoreAssessmentId &&
      assessmentScoreOptions.length > 0 &&
      !assessmentScoreOptions.some((o) => o.id === scoreAssessmentId)
    ) {
      setScoreAssessmentId("");
    }
  }, [assessmentScoreOptions, scoreAssessmentId]);

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleFilterChange = (
    section: IFilter,
    changedOption: filterOption
  ) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.id === section.id) {
        const updatedOptions = filter.options.map((option) => {
          if (option.value === changedOption.value) {
            return { ...option, checked: !option.checked };
          }
          if (changedOption.value !== "" && option.value === "")
            return { ...option, checked: false };
          if (changedOption.value === "") return { ...option, checked: false };
          else return option;
        });
        return { ...filter, options: updatedOptions };
      }
      return filter;
    });

    let noneStatusSelected = true;
    let allStatusIsSelected = false;
    updatedFilters[0].options.forEach((uf, ind) => {
      if (uf.checked) {
        if (ind === 0) allStatusIsSelected = true;
        noneStatusSelected = false;
        return;
      }
    });
    const updatedBranchInd = updatedFilters.findIndex((f) => f.id === "branches");

    if (updatedBranchInd !== -1) {
      const noneBranchSelected = updatedFilters[updatedBranchInd].options.every((o) => !o.checked);
      if (noneBranchSelected) {
        updatedFilters[updatedBranchInd].options[0].checked = true;
      }
    }

    if (allStatusIsSelected || noneStatusSelected) {
      updatedFilters[0].options[0].checked = true;
      handleStatusFilterIsAll(true);
    } else handleStatusFilterIsAll(false);
    setFilters(updatedFilters);
  };

  const handleGradeFilterChange = (
    section: IGradeFilter,
    changedOption: filterOption
  ) => {
    const updatedFilters = gradeFilters.map((filter) => {
      if (filter.id === section.id) {
        const updatedOptions = filter.options.map((option) => {
          if (option.value === changedOption.value) {
            return { ...option, checked: !option.checked };
          } else return option;
        });
        return { ...filter, options: updatedOptions };
      }
      return filter;
    });
    setGradeFilters(updatedFilters);
  };

  const handleLocationFilterChange = (changedOption: filterOption) => {
    const updatedOptions = locationFilter.options.map((option) => {
      if (option.value === changedOption.value) {
        return { ...option, checked: !option.checked };
      }
      return option;
    });
    const updatedFilteredOptions = locationFilter.filteredOptions.map((fo) => {
      if (fo.value === changedOption.value) {
        return { ...fo, checked: !fo.checked };
      }
      return fo;
    });
    setLocationFilter((prev) => ({
      ...prev,
      options: updatedOptions,
      filteredOptions: updatedFilteredOptions,
    }));
  };

  const handleUniversityNameFilterChange = (changedOption: filterOption) => {
    const updatedOptions = universityNameFilter.options.map((option) => {
      if (option.value === changedOption.value) {
        return { ...option, checked: !option.checked };
      }
      return option;
    });
    const updatedFilteredOptions = universityNameFilter.filteredOptions.map((fo) => {
      if (fo.value === changedOption.value) {
        return { ...fo, checked: !fo.checked };
      }
      return fo;
    });
    setUniversityNameFilter((prev) => ({
      ...prev,
      options: updatedOptions,
      filteredOptions: updatedFilteredOptions,
    }));
  };

  const handleCompanyNameFilterChange = (changedOption: filterOption) => {
    const updatedOptions = companyNameFilter.options.map((option) => {
      if (option.value === changedOption.value) {
        return { ...option, checked: !option.checked };
      }
      return option;
    });
    const updatedFilteredOptions = companyNameFilter.filteredOptions.map((fo) => {
      if (fo.value === changedOption.value) {
        return { ...fo, checked: !fo.checked };
      }
      return fo;
    });
    setCompanyNameFilter((prev) => ({
      ...prev,
      options: updatedOptions,
      filteredOptions: updatedFilteredOptions,
    }));
  };

  const handleRangeChange = (
    section: IRangeFilter,
    value: { min: number; max: number },
    current = false
  ) => {
    const updatedRangeFilter = rangeFilters.map((rf) => {
      if (rf.id === section.id) return { ...rf, currentVal: value };
      else return rf;
    });
    if (current) {
      setCurrentRangeFilter(updatedRangeFilter);
    } else {
      setRangeFilter(updatedRangeFilter);
    }
  };

  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const delayedSearch = debounce((query: string) => {
    handleSearchQuery(query);
  }, 1000);

  // Load universities when filter is opened
  const loadUniversities = async () => {
    if (!token || universitiesLoaded || universitiesLoading) return;

    setUniversitiesLoading(true);
    try {
      const data = await getUniversitiesForFilter(token);
      setUniversities(data);
      setUniversitiesLoaded(true);
    } catch (error) {
      console.error('Error loading universities:', error);
    } finally {
      setUniversitiesLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters(resetFilter);
    setLocationFilter((prev) => {
      const resetOptions = prev.options.map((o) => ({
        ...o,
        checked: false,
      }));
      return {
        ...prev,
        options: resetOptions,
        filteredOptions: resetOptions,
      };
    });
    setUniversityNameFilter((prev) => {
      const resetOptions = prev.options.map((o) => ({
        ...o,
        checked: false,
      }));
      return {
        ...prev,
        options: resetOptions,
        filteredOptions: resetOptions,
      };
    });
    setCompanyNameFilter((prev) => {
      const resetOptions = prev.options.map((o) => ({
        ...o,
        checked: false,
      }));
      return {
        ...prev,
        options: resetOptions,
        filteredOptions: resetOptions,
      };
    });
    setDateFilter(initialDateFilter);
    setRangeFilter(initialRangeFilters);
    setCurrentRangeFilter(initialRangeFilters);
    const filter: IGradeFilter[] = gradeFilters.map((g) => ({
      id: g.id,
      name: g.name,
      roleStatusTypeName: g.roleStatusTypeName,
      options: [
        { value: 100, label: "Excellent", checked: false },
        { value: 89, label: "Pass", checked: false },
        { value: 79, label: "Fail", checked: false },
        { value: 50, label: "Waitlist", checked: false },
      ],
    }));
    setGradeFilters(filter);
    setSelectedRanks(undefined);
    setSelectedShippingDepartments(undefined);
    setIsOnBoardExp(undefined);
    setScoreAssessmentId("");
    clearFiltersFromStorage(roleId);
  };

  /**
   * activeFilterLabels
   */

  const activeFilterLabels = useMemo(() => [
    // 1. Main checkbox filters (e.g. Position: Working, Slot: Booked)
    ...filters.flatMap(s => s.options
      .filter(o => o.checked && o.value !== "" && o.value !== "all")
      .map(o => ({ label: `${s.name}: ${o.label}`, onRemove: () => handleFilterChange(s, o) }))),
    // 2. Grade filters 
    ...gradeFilters.flatMap(s => s.options
      .filter(o => o.checked)
      .map(o => ({ label: `${s.name}: ${o.label}`, onRemove: () => handleGradeFilterChange(s, o) }))),
    // 3. Location filter 
    ...locationFilter.options.filter(o => o.checked)
      .map(o => ({ label: `Location: ${o.label}`, onRemove: () => handleLocationFilterChange(o) })),
    // 4. University name filter 
    ...universityNameFilter.options.filter(o => o.checked)
      .map(o => ({ label: `University: ${o.label}`, onRemove: () => handleUniversityNameFilterChange(o) })),
    // 5. Company name filter 
    ...companyNameFilter.options.filter(o => o.checked)
      .map(o => ({ label: `Company: ${o.label}`, onRemove: () => handleCompanyNameFilterChange(o) })),
    // 6. Range filters — 
    ...rangeFilters.filter(f => f.currentVal.min !== f.minVal || f.currentVal.max !== f.maxVal)
      .map(f => {
        // Name the assessment in the pill so it is obvious which score is being filtered.
        const target = f.id === "score"
          ? assessmentScoreOptions.find(o => o.id === scoreAssessmentId)?.name ?? "Overall"
          : null;
        const name = target ? `${f.name} (${target})` : f.name;
        return {
          label: `${name}: ${f.currentVal.min}–${f.currentVal.max}`,
          onRemove: () => {
            setRangeFilter(p => p.map(r => r.id === f.id ? { ...r, currentVal: { min: r.minVal, max: r.maxVal } } : r));
            setCurrentRangeFilter(p => p.map(r => r.id === f.id ? { ...r, currentVal: { min: r.minVal, max: r.maxVal } } : r));
          },
        };
      }),
    // 7. Date filter — 
    ...(dateFilter.startValue || dateFilter.endValue ? [{ label: `Date: ${dateFilter.startValue?.toLocaleDateString() ?? ""} – ${dateFilter.endValue?.toLocaleDateString() ?? ""}`, onRemove: () => setDateFilter(initialDateFilter) }] : []),
    // 8. Shipping rank selections 
    ...(selectedRanks ?? []).map(r => ({ label: `Rank: ${r}`, onRemove: () => setSelectedRanks(p => p?.filter(x => x !== r)) })),
    // 9. Shipping department selections 
    ...(selectedShippingDepartments ?? []).map(d => ({ label: `Dept: ${d}`, onRemove: () => setSelectedShippingDepartments(p => p?.filter(x => x !== d)) })),
    // 10. Onboard / Onshore experience type toggle
    ...(isOnBoardExp !== undefined ? [{ label: `Exp: ${isOnBoardExp ? "Onboard" : "Onshore"}`, onRemove: () => setIsOnBoardExp(undefined) }] : []),
  ], [filters, gradeFilters, locationFilter, universityNameFilter, companyNameFilter, rangeFilters, dateFilter, selectedRanks, selectedShippingDepartments, isOnBoardExp, assessmentScoreOptions, scoreAssessmentId]);

  return (
    <div className="px-4">
      <div className="relative flex flex-grow focus-within:z-10 mt-2 items-center justify-between">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
          <MagnifyingGlassIcon
            className="h-5 w-5 text-gray-400 dark:text-gray-300"
            aria-hidden="true"
          />
        </div>
        <input
          type="text"
          name="search"
          id="search"
          className="block w-10/12 border-0 pl-9 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
          placeholder="Quick Search"
          onChange={(e) => {
            delayedSearch(e.target.value);
          }}
        />
        <div
          className="text-gray-400 hover:text-gray-500 cursor-pointer relative dark:hover:text-gray-300"
          onClick={handleResetFilters}
          title="Reset all filters"
        >
          <FunnelIcon width={24} />
          <ArrowPathIcon width={14} className="absolute bottom-0 -right-1" />
          <FilterBadge count={activeFilterLabels.length} />
        </div>
      </div>


      {/* Active Filter Tags (filters) */}

      {activeFilterLabels.length > 0 && (
        <div className="w-[90%] mb-3 mt-1">
          {/* Total active filter count badge */}
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{activeFilterLabels.length} Active</span>
          {/* Individual filter pills — one per active filter option */}
          <div className="flex flex-wrap gap-1 mt-1">
            {activeFilterLabels.map((af, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 rounded-full px-2.5 py-1 border border-orange-200 dark:border-orange-700 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400 shrink-0" />
                {af.label}
                {/* Remove button — deselects this specific filter option */}
                <button type="button" onClick={af.onRemove} className="ml-0.5 font-bold hover:text-orange-600 dark:hover:text-orange-300" title={`Remove ${af.label}`}>✕</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {filters.map(
        (section, ind) => {
          const isOpen = openSections[section.id] ?? false;
          if (!isShipping && section.id === "isShippingCompany") {
            return null;
          }
          return (
            <Fragment key={section.id}>
              <Disclosure
                as="div"
                className="border-b border-gray-200 py-4 dark:border-gray-500"
              >
                {({ open, close }) => (
                  <div className="relative">
                    <h3 className="-my-4 flow-root">
                      <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200">
                        <span className="font-medium text-gray-900 dark:text-gray-300">
                          {section.name}
                        </span>
                        <span className="ml-6 flex items-center">
                          {open ? (
                            <MinusIcon className="h-5 w-5" aria-hidden="true" />
                          ) : (
                            <PlusIcon className="h-5 w-5" aria-hidden="true" />
                          )}
                        </span>
                      </Disclosure.Button>
                    </h3>

                    <Disclosure.Panel
                      className={`${ind === 0
                        ? "pt-4 relative "
                        : "py-3 px-4 absolute right-4 bg-white rounded-md z-50 border border-gray-200 shadow-sm dark:bg-darkbglight dark:border-gray-500 max-h-80 overflow-y-auto"
                        } h-fit`}
                    >
                      {section.options.length === 0 ? (
                        <div className="text-xs">No filters available</div>
                      ) : (
                        <div className={`space-y-2`}>
                          {section.options.map((option, optionIdx) => (
                            <div key={optionIdx} className="flex items-center">
                              <input
                                id={`filter-${section.id}-${optionIdx}`}
                                name={`${section.id}[]`}
                                type={
                                  option.value === "" ? "radio" : "checkbox"
                                }
                                onChange={() => {
                                  handleFilterChange(section, option);
                                }}
                                checked={option.checked}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />

                              <label
                                htmlFor={`filter-${section.id}-${optionIdx}`}
                                className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                              >
                                <span>{option.label}</span>
                                {option.extraData !== undefined && (
                                  <span
                                    className={`ml-2 hidden rounded-full py-0.5 px-2.5 text-xs font-medium md:inline-block ${option.checked
                                      ? "bg-blue-100 text-entntblue"
                                      : "bg-gray-100 text-gray-900 dark:bg-darkbglight dark:text-gray-400"
                                      }`}
                                  >
                                    {option.extraData}
                                  </span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </Disclosure.Panel>
                    {ind !== 0 && open && (
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => {
                          close();
                        }}
                      ></div>
                    )}
                  </div>
                )}
              </Disclosure>
              {/* University Name Filter - Show after University ranking filter */}
              {section.id === "universityRankings" && (
                <Disclosure
                  as="div"
                  className="border-b border-gray-200 py-4 dark:border-gray-500"
                >
                  {({ open, close }) => (
                    <div className="relative">
                      <h3 className="-my-4 flow-root">
                        <Disclosure.Button
                          className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                          onClick={() => {
                            if (open) {
                              setUniversityNameSearch("");
                            } else {
                              // Load universities when opening the filter
                              loadUniversities();
                            }
                          }}
                        >
                          <span className="font-medium text-gray-900 dark:text-gray-300">
                            {universityNameFilter.name}
                          </span>
                          <span className="ml-6 flex items-center">
                            {open ? (
                              <MinusIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            ) : (
                              <PlusIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                        </Disclosure.Button>
                      </h3>

                      <Disclosure.Panel className="py-3 px-4 absolute right-4 bg-white rounded-md z-50 border border-gray-200 shadow-sm dark:bg-darkbglight dark:border-gray-500 max-h-80 overflow-y-auto">
                        <div className="relative mb-3">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                            <MagnifyingGlassIcon
                              className="h-4 w-4 text-gray-400 dark:text-gray-300"
                              aria-hidden="true"
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Search University"
                            className="w-full border-none pl-8 pr-3 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
                            onChange={(event) =>
                              setUniversityNameSearch(event.target.value)
                            }
                            value={universityNameSearch}
                            disabled={universitiesLoading}
                          />
                        </div>
                        {universitiesLoading ? (
                          <div className="text-xs text-gray-500 px-2 py-1">
                            Loading universities...
                          </div>
                        ) : universityNameFilter.filteredOptions.length ===
                          0 ? (
                          <div className="text-xs text-gray-500 px-2 py-1">
                            No universities found
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {universityNameFilter.filteredOptions.map(
                              (option, optionIdx) => (
                                <div
                                  key={optionIdx}
                                  className="flex items-center"
                                >
                                  <input
                                    id={`university-filter-${optionIdx}`}
                                    type="checkbox"
                                    onChange={() => {
                                      handleUniversityNameFilterChange(option);
                                    }}
                                    checked={option.checked}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <label
                                    htmlFor={`university-filter-${optionIdx}`}
                                    className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                                  >
                                    <span>{option.label}</span>
                                  </label>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </Disclosure.Panel>
                      {open && (
                        <div
                          className="fixed inset-0 z-20"
                          onClick={() => {
                            close();
                            setUniversityNameSearch("");
                          }}
                        ></div>
                      )}

                      {/* Show selected university chips */}
                      {section.id === "universityRankings" &&
                        universityNameFilter.options.some((o) => o.checked) && (
                          <div className="text-xs flex flex-wrap box-border -mb-2">
                            {universityNameFilter.options.map((o, index) =>
                              o.checked ? (
                                <span
                                  key={o.label + index}
                                  title={o.label}
                                  className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
                                >
                                  <span className="truncate w-10">
                                    {o.label}
                                  </span>
                                  <XMarkIcon
                                    onClick={() => {
                                      handleUniversityNameFilterChange(o);
                                    }}
                                    className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                                  />
                                </span>
                              ) : (
                                <Fragment key={index}></Fragment>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </Disclosure>
              )}

              {/* Company Name Filter - Show after Company ranking filter */}
              {section.id === "companyRankings" && (
                <Disclosure
                  as="div"
                  className="border-b border-gray-200 py-4 dark:border-gray-500"
                >
                  {({ open, close }) => (
                    <div className="relative">
                      <h3 className="-my-4 flow-root">
                        <Disclosure.Button
                          className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
                          onClick={() => {
                            if (open) {
                              setCompanyNameSearch("");
                            }
                          }}
                        >
                          <span className="font-medium text-gray-900 dark:text-gray-300">
                            {companyNameFilter.name}
                          </span>
                          <span className="ml-6 flex items-center">
                            {open ? (
                              <MinusIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            ) : (
                              <PlusIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                        </Disclosure.Button>
                      </h3>

                      <Disclosure.Panel className="py-3 px-4 absolute right-4 bg-white rounded-md z-50 border border-gray-200 shadow-sm dark:bg-darkbglight dark:border-gray-500 max-h-80 overflow-y-auto">
                        <div className="relative mb-3">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                            <MagnifyingGlassIcon
                              className="h-4 w-4 text-gray-400 dark:text-gray-300"
                              aria-hidden="true"
                            />
                          </div>
                          <input
                            ref={companySearchInputRef}
                            type="text"
                            placeholder="Type to search companies..."
                            className={`w-full border-none pl-8 pr-3 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md ${companiesLoading ? 'opacity-70' : ''}`}
                            onChange={(event) =>
                              setCompanyNameSearch(event.target.value)
                            }
                            value={companyNameSearch}
                          />
                        </div>
                        {companiesLoading ? (
                          <div className="text-xs text-gray-500 px-2 py-1">
                            Searching companies...
                          </div>
                        ) : companyNameFilter.filteredOptions.length === 0 ? (
                          <div className="text-xs text-gray-500 px-2 py-1">
                            {companyNameSearch.length < 2
                              ? "Type at least 2 characters to search"
                              : "No companies found"}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {companyNameFilter.filteredOptions.map(
                              (option, optionIdx) => (
                                <div
                                  key={optionIdx}
                                  className="flex items-center"
                                >
                                  <input
                                    id={`company-filter-${optionIdx}`}
                                    type="checkbox"
                                    onChange={() => {
                                      handleCompanyNameFilterChange(option);
                                    }}
                                    checked={option.checked}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <label
                                    htmlFor={`company-filter-${optionIdx}`}
                                    className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                                  >
                                    <span>{option.label}</span>
                                  </label>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </Disclosure.Panel>
                      {open && (
                        <div
                          className="fixed inset-0 z-20"
                          onClick={() => {
                            close();
                            setCompanyNameSearch("");
                          }}
                        ></div>
                      )}

                      {section.id === "companyRankings" &&
                        companyNameFilter.options.some((o) => o.checked) && (
                          <div className="text-xs flex flex-wrap box-border -mb-2">
                            {companyNameFilter.options.map((o, index) =>
                              o.checked ? (
                                <span
                                  key={o.label + index}
                                  title={o.label}
                                  className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
                                >
                                  <span className="w-10 truncate">
                                    {o.label}
                                  </span>
                                  <XMarkIcon
                                    onClick={() => {
                                      handleCompanyNameFilterChange(o);
                                    }}
                                    className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                                  />
                                </span>
                              ) : (
                                <Fragment key={index}></Fragment>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </Disclosure>
              )}
            </Fragment>
          );
        }
      )}
      {/* Experience Filter */}
      {isShipping && (
        <Disclosure
          as="div"
          className="border-b border-gray-200 py-4 dark:border-gray-500"
          defaultOpen={openSections['shippingExp']}
        >
          {({ open, close }) => (
            <>
              <h3 className="-my-4 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
                  <span className="font-medium text-gray-900 dark:text-gray-300">
                    Shipping Exp Filter
                  </span>
                  <span className="ml-6 flex items-center">
                    {open ? (
                      <MinusIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <PlusIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </span>
                </Disclosure.Button>
              </h3>
              <Disclosure.Panel className="pl-6 pt-2">
                <form className="flex items-center justify-between gap-2 mx-auto">
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    Job Type
                  </label>
                  <select
                    defaultValue="both"
                    onChange={(e) => {
                      const value = e.target.value;
                      setIsOnBoardExp(
                        value === "1" ? true : value === "0" ? false : undefined
                      );
                      setSelectedRanks(undefined);
                      setSelectedShippingDepartments(undefined);
                      setSelectedRank(undefined);
                      setSelectedShippingDepartment(undefined);
                    }}
                    className="bg-gray-50 h-8 p-0 pl-2 pr-8 dark:bg-darkbg text-gray-900 text-sm rounded-lg dark:text-white"
                  >
                    <option value="both">Both</option>
                    <option value="1">OnBoard</option>
                    <option value="0">Ashore</option>
                  </select>
                </form>
                {isOnBoardExp ? (
                  <Disclosure
                    as="div"
                    className="border-b border-gray-200 py-4 dark:border-gray-500"
                  >
                    {({ open, close }) => (
                      <div>
                        <div className="relative">
                          <h3 className="-my-4 flow-root">
                            <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
                              <span className="font-medium text-gray-900 dark:text-gray-300">
                                Rank
                              </span>
                              <span className="ml-3 flex items-center">
                                <div className="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left ring-1 ring-gray-300 sm:text-sm dark:ring-gray-500">
                                  <input
                                    type="text"
                                    placeholder="Search Rank"
                                    onChange={(e) => {
                                      setSelectedRank(e.target.value);
                                    }}
                                    className="w-full border-none py-1 pl-3 pr-10 text-sm text-gray-900 focus:ring-0 placeholder:text-gray-400 dark:text-gray-300 dark:bg-darkbg"
                                  />
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon
                                      className="h-5 w-5 text-gray-400 dark:text-gray-400"
                                      aria-hidden="true"
                                    />
                                  </div>
                                </div>
                              </span>
                            </Disclosure.Button>
                          </h3>

                          <Disclosure.Panel
                            className={`dark:bg-darkbglight dark:border-gray-500 shadow-sm pl-1 py-1 h-fit absolute bg-white rounded-md z-50 border border-gray-200 top-[3rem] -right-[0.20rem]`}
                          >
                            <div className="max-h-[10.5rem] min-w-[11.8rem] pl-2 py-1 overflow-auto thin-scroll bg-transparent z-10">
                              <div className="space-y-2">
                                {ranks.map(
                                  (option, optionIdx) =>
                                    option.includes(selectedRank ?? "") && (
                                      <div
                                        key={optionIdx}
                                        className="flex items-center"
                                      >
                                        <input
                                          id={`locaiton-filter-${optionIdx}`}
                                          type={"checkbox"}
                                          onChange={(e) => {
                                            setSelectedRanks((prev) => [
                                              ...(prev || []),
                                              option,
                                            ]);
                                            close();
                                          }}
                                          checked={
                                            selectedRanks &&
                                            selectedRanks.includes(option)
                                          }
                                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />

                                        <label
                                          id={`locaiton-filter-${optionIdx}`}
                                          className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                                        >
                                          <span>{option}</span>
                                        </label>
                                      </div>
                                    )
                                )}
                              </div>
                            </div>
                          </Disclosure.Panel>
                        </div>
                        {open && (
                          <div
                            className="fixed inset-0 z-20"
                            onClick={() => {
                              close();
                            }}
                          ></div>
                        )}
                        <div className="z-10 text-xs flex flex-wrap box-border mt-2">
                          {selectedRanks &&
                            selectedRanks.map((o, index) => (
                              <span
                                key={index}
                                className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
                              >
                                {o}
                                <XMarkIcon
                                  onClick={() => {
                                    setSelectedRanks(
                                      selectedRanks.filter((e) => e != o)
                                    );
                                  }}
                                  className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                                />
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                  </Disclosure>
                ) : (
                  isOnBoardExp !== undefined && (
                    <Disclosure
                      as="div"
                      className="border-b border-gray-200 py-4 dark:border-gray-500"
                    >
                      {({ open, close }) => (
                        <div>
                          <div className="relative">
                            <h3 className="-my-4 flow-root">
                              <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
                                <span className="font-medium text-gray-900 dark:text-gray-300">
                                  Department
                                </span>
                                <span className="ml-3 flex items-center">
                                  <div className="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left ring-1 ring-gray-300 sm:text-sm dark:ring-gray-500">
                                    <input
                                      type="text"
                                      placeholder="Search Department"
                                      onChange={(e) =>
                                        setSelectedShippingDepartment(
                                          e.target.value
                                        )
                                      }
                                      className="w-full border-none py-1 pl-3 pr-10 text-sm text-gray-900 focus:ring-0 placeholder:text-gray-400 dark:text-gray-300 dark:bg-darkbg"
                                    />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                                      <ChevronUpDownIcon
                                        className="h-5 w-5 text-gray-400 dark:text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </div>
                                  </div>
                                </span>
                              </Disclosure.Button>
                            </h3>

                            <Disclosure.Panel
                              className={`dark:bg-darkbglight dark:border-gray-500 shadow-sm pl-1 py-1 h-fit absolute bg-white rounded-md z-50 border border-gray-200 top-[3rem] -right-[0.20rem]`}
                            >
                              <div className="max-h-[10.5rem] min-w-[11.8rem] pl-2 py-1 overflow-auto thin-scroll bg-transparent z-10">
                                <div className="space-y-2">
                                  {shippingDepartments.map(
                                    (option, optionIdx) =>
                                      option.includes(
                                        selectedShippingDepartment ?? ""
                                      ) && (
                                        <div
                                          key={optionIdx}
                                          className="flex items-center"
                                        >
                                          <input
                                            id={`locaiton-filter-${optionIdx}`}
                                            type={"checkbox"}
                                            onChange={(e) => {
                                              setSelectedShippingDepartments(
                                                (prev) => [
                                                  ...(prev || []),
                                                  option,
                                                ]
                                              );
                                              close();
                                            }}
                                            checked={
                                              selectedShippingDepartments &&
                                              selectedShippingDepartments.includes(
                                                option
                                              )
                                            }
                                            value={selectedRank}
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                          />

                                          <label
                                            id={`locaiton-filter-${optionIdx}`}
                                            className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                                          >
                                            <span>{option}</span>
                                          </label>
                                        </div>
                                      )
                                  )}
                                </div>
                              </div>
                            </Disclosure.Panel>
                          </div>
                          {open && (
                            <div
                              className="fixed inset-0 z-20"
                              onClick={() => {
                                close();
                              }}
                            ></div>
                          )}
                          <div className="z-10 text-xs flex flex-wrap box-border mt-2">
                            {selectedShippingDepartments &&
                              selectedShippingDepartments.map((o, index) => (
                                <span
                                  key={o}
                                  className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
                                >
                                  {o}
                                  <XMarkIcon
                                    onClick={() => {
                                      setSelectedShippingDepartments(
                                        selectedShippingDepartments.filter(
                                          (e) => e != o
                                        )
                                      );
                                    }}
                                    className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                                  />
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </Disclosure>
                  )
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}
      <Disclosure
        as="div"
        className="border-b border-gray-200 py-4 dark:border-gray-500"
        defaultOpen={openSections['grade']}
      >
        {({ open, close }) => (
          <div className="relative">
            <h3 className="-my-4 flow-root">
              <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
                <span className="font-medium text-gray-900 dark:text-gray-300">
                  Grade
                </span>
                <span className="ml-6 flex items-center">
                  {open ? (
                    <MinusIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </span>
              </Disclosure.Button>
            </h3>

            <Disclosure.Panel className="py-2 px-4 pb-3 absolute right-4 bg-white rounded-md z-50 border border-gray-200 shadow-lg h-fit dark:bg-darkbglight dark:border-gray-500">
              {gradeFilters.map((section) => (
                <div key={section.id} className="py-1 divide-y-2">
                  <div className="relative">
                    <h3 className="flow-root">
                      <span className="font-medium text-sm text-gray-600 dark:text-gray-400">
                        {section.name}
                      </span>
                    </h3>

                    <div className={`flex items-center space-x-3`}>
                      {section.options.map((option, optionIdx) => (
                        <div key={optionIdx} className="flex items-center">
                          <label
                            htmlFor={`filter-${section.id}-${optionIdx}`}
                            className="text-xs text-gray-600 flex justify-between w-full pr-1 dark:text-gray-400"
                          >
                            {option.label}
                          </label>
                          <input
                            id={`gradeFilter-${section.id}-${optionIdx}`}
                            name={`${section.id}[]`}
                            type="checkbox"
                            onChange={() => {
                              handleGradeFilterChange(section, option);
                            }}
                            checked={option.checked}
                            className="h-3 w-3 rounded border-gray-300 text-indigo-600"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </Disclosure.Panel>
            {open && (
              <div
                className="fixed inset-0 z-20"
                onClick={() => {
                  close();
                }}
              ></div>
            )}
          </div>
        )}
      </Disclosure>
      {currentRangeFilters.map((section) => (
        <Disclosure
          as="div"
          key={section.id}
          className="border-b border-gray-200 py-4 dark:border-gray-500"
          defaultOpen={openSections[section.id]}
        >
          {({ open }) => (
            <>
              <h3 className="-my-4 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
                  <span className="font-medium text-gray-900 dark:text-gray-300">
                    {section.name}
                  </span>
                  <span className="ml-6 flex items-center">
                    {open ? (
                      <MinusIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <PlusIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </span>
                </Disclosure.Button>
              </h3>
              <Disclosure.Panel className="pt-6 pb-4 px-6">
                <div>
                  {/* Which assessment this score range applies to. Without it the range was
                      silently matched against a blended role-level score, so on a role with
                      several assessment stages there was no way to tell what was filtered. */}
                  {section.id === "score" && assessmentScoreOptions.length > 0 && (
                    <div className="mb-5">
                      <label
                        htmlFor="score-assessment-select"
                        className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
                      >
                        Assessment
                      </label>
                      <select
                        id="score-assessment-select"
                        value={scoreAssessmentId}
                        onChange={(e) => setScoreAssessmentId(e.target.value)}
                        className="w-full rounded-md border-0 py-1 pl-2 pr-8 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-inset focus:ring-entntorange bg-transparent dark:text-gray-300 dark:ring-gray-500 dark:bg-darkbglight"
                      >
                        <option value="">Overall (all assessments)</option>
                        {assessmentScoreOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div key={section.id} className="flex items-center">
                    {section.id === "upperage" ? (
                      <Slider
                        range
                        max={section.maxVal}
                        min={0}
                        value={[0, section.currentVal.max]}
                        onChange={(value) => {
                          const [min, max] = value as number[];
                          if (min >= section.minVal && max <= section.maxVal) {
                            handleRangeChange(section, { min, max }, true);
                          }
                        }}
                        onChangeComplete={(value) => {
                          const [min, max] = value as number[];
                          if (min >= section.minVal && max <= section.maxVal) {
                            handleRangeChange(section, { min, max });
                          }
                        }}
                        marks={{
                          [section.minVal]: section.minVal,
                          [section.maxVal]: section.maxVal,
                        }}
                        styles={{
                          track: {
                            background: "#0D42EC",
                          },
                          handle: {
                            borderColor: "#0D42EC",
                          },
                        }}
                      />
                    ) : (
                      <Slider
                        range
                        max={section.maxVal}
                        min={section.minVal}
                        step={section.step ?? 1}
                        value={[section.currentVal.min, section.currentVal.max]}
                        onChange={(value) => {
                          const [min, max] = value as number[];
                          if (min >= section.minVal && max <= section.maxVal) {
                            handleRangeChange(
                              section,
                              { min: roundScore(min), max: roundScore(max) },
                              true
                            );
                          }
                        }}
                        onChangeComplete={(value) => {
                          const [min, max] = value as number[];
                          if (min >= section.minVal && max <= section.maxVal) {
                            handleRangeChange(section, {
                              min: roundScore(min),
                              max: roundScore(max),
                            });
                          }
                        }}
                        marks={{
                          [section.minVal]: section.minVal,
                          [section.maxVal]: section.maxVal,
                        }}
                        styles={{
                          track: {
                            background: "#0D42EC",
                          },
                          handle: {
                            borderColor: "#0D42EC",
                          },
                        }}
                      />
                    )}
                  </div>
                  {section.allowDecimalInput && (
                    <RangeNumberInputs
                      section={section}
                      onCommit={(value) => {
                        handleRangeChange(section, value, true);
                        handleRangeChange(section, value);
                      }}
                    />
                  )}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ))}

      <Disclosure
        as="div"
        className="border-b border-gray-200 py-4 dark:border-gray-500"
        defaultOpen={openSections[locationFilter.id]}
      >
        {({ open, close }) => (
          <div>
            <div className="relative">
              <h3 className="-my-4 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
                  <span className="font-medium text-gray-900 dark:text-gray-300">
                    {locationFilter.name}
                  </span>
                  <span className="ml-3 flex items-center">
                    <div className="relative w-full cursor-default overflow-hidden rounded-md bg-white text-left ring-1 ring-gray-300 sm:text-sm dark:ring-gray-500">
                      <input
                        type="text"
                        placeholder="Search Location"
                        className="w-full border-none py-1 pl-3 pr-10 text-sm text-gray-900 focus:ring-0 placeholder:text-gray-400 dark:text-gray-300 dark:bg-darkbg"
                        onChange={(event) =>
                          setLocationSearch(event.target.value)
                        }
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-400 dark:text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </span>
                </Disclosure.Button>
              </h3>

              <Disclosure.Panel
                className={`dark:bg-darkbglight dark:border-gray-500 shadow-sm pl-1 py-1 h-fit absolute bg-white rounded-md z-50 border border-gray-200 bottom-[2.6rem] -right-[0.20rem]`}
              >
                <div className="max-h-[10.5rem] min-w-[11.8rem] pl-2 py-1 overflow-auto thin-scroll bg-transparent z-10">
                  <div className="space-y-2">
                    {locationFilter.filteredOptions.map((option, optionIdx) => (
                      <div key={optionIdx} className="flex items-center">
                        <input
                          id={`locaiton-filter-${optionIdx}`}
                          type={"checkbox"}
                          onChange={() => {
                            handleLocationFilterChange(option);
                          }}
                          checked={option.checked}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />

                        <label
                          id={`locaiton-filter-${optionIdx}`}
                          className="ml-3 text-sm text-gray-600 flex justify-between w-full pr-2 dark:text-gray-400"
                        >
                          <span>{option.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </Disclosure.Panel>
            </div>
            {open && (
              <div
                className="fixed inset-0 z-20"
                onClick={() => {
                  close();
                }}
              ></div>
            )}
            <div className="z-10 text-xs flex flex-wrap box-border mt-2">
              {locationFilter.options.map((o, index) =>
                o.checked ? (
                  <span
                    key={o.label + index}
                    className="flex items-center bg-blue-100 text-entntblue py-1 px-2 mr-2 mt-2 rounded-full relative"
                  >
                    {o.label}
                    <XMarkIcon
                      onClick={() => {
                        handleLocationFilterChange(o);
                      }}
                      className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:text-gray-600"
                    />
                  </span>
                ) : (
                  <Fragment key={index}></Fragment>
                )
              )}
            </div>
          </div>
        )}
      </Disclosure>

      <div className="border-b border-gray-200 py-4 dark:border-gray-500">
        <h3 className="-my-3 flow-root">
          <div className="flex w-full items-center justify-between py-3 text-sm text-gray-400 hover:text-gray-500">
            <span className="font-medium text-gray-900 dark:text-gray-300">
              {dateFilter.name}
            </span>
            <span className="flex items-center">
              <div className="flex items-center">
                <ReactDatePicker
                  selected={dateFilter.startValue}
                  onChange={(date) => {
                    const [start, end] = date;
                    end && end.setHours(23, 59, 59, 999);
                    setDateFilter({
                      ...dateFilter,
                      startValue: start,
                      endValue: end,
                    });
                  }}
                  selectsRange
                  isClearable
                  showIcon
                  showYearDropdown
                  scrollableYearDropdown
                  dropdownMode="select"
                  startDate={dateFilter.startValue}
                  endDate={dateFilter.endValue}
                  icon={<CalendarDaysIcon className="text-gray-400" />}
                  className="placeholder:text-gray-400 text-sm rounded-md border-none ring-1 ring-inset ring-gray-300 block w-[13rem] align-bottom cursor-pointer py-1 dark:bg-darkbg dark:ring-gray-500"
                  placeholderText="  Select period"
                  calendarClassName="datepicker-calendar"
                  wrapperClassName="datepicker-wrapper"
                />
              </div>
            </span>
          </div>
        </h3>
      </div>
    </div>
  );
}
